/**
 * Refinery page rows: the words and numbers beside each test and task.
 *
 * Kept apart from the components so the labels can be tested against the
 * copy gate (no em dashes, no participial tails) and the words stay in one
 * place. The api decides what is proven; this file only says it in words.
 */

import type {
  RefineryBasePolicyValidation,
  RefineryFailureAnalysisVerdict,
  RefineryGeneration,
  RefineryNightly,
  RefineryRegenerate,
  RefineryTask,
  RefineryTest,
} from '@/api/generated/model'

/** What names a task's case to the api, and so one task on the page. */
export type TaskCase = Pick<RefineryTask, 'batch' | 'record_id' | 'case'>

/** What the REFERENCE cell says: "2 of 3". */
export function passesLabel(task: Pick<RefineryTask, 'passes' | 'runs'>): string {
  return `${task.passes.toString()} of ${task.runs.toString()}`
}

/** What the BASE POLICY cell says: "1 of 3", or that the gate has not run it. */
export function basePolicyLabel(task: RefineryTask): string {
  return task.base_policy === null ? REFINERY_COPY.notMeasured : passesLabel(task.base_policy)
}

/** What the NIGHTLY cell says: nights passed over nights read, or why there are none. */
export function nightlyLabel(nightly: RefineryNightly | null): string {
  if (nightly === null) return REFINERY_COPY.noEvalModel
  return `${nightly.passed.toString()} of ${nightly.nights.toString()}`
}

/** What a test's Verdict cell says: its latest round's verdict, or that it has had no round. */
export type TestVerdict = RefineryFailureAnalysisVerdict | 'noRound'

export function testVerdict(test: Pick<RefineryTest, 'failure_analysis'>): TestVerdict {
  return test.failure_analysis === null ? 'noRound' : test.failure_analysis.verdict
}

/** The accessible name of the link from a Verdict cell to the record of the test. */
export function openRecordLabel(test: Pick<RefineryTest, 'family' | 'failure_analysis'>): string {
  return `${REFINERY_COPY.verdicts[testVerdict(test)]}: open the record of ${test.family}`
}

/** What the base policy scored on the test before any training step, as a sentence. */
export function stepZeroSentence(validation: RefineryBasePolicyValidation | null): string {
  if (validation === null) return REFINERY_COPY.noStepZero
  const over = counted(validation.samples, 'sample')
  return `The base policy scored ${validation.reward.toFixed(3)} at step 0 over ${over} in run ${validation.run_id}.`
}

/**
 * What the reviewer made of the cards generation planned from the test, as
 * a sentence, or null when no start recorded a planned card although tasks
 * sit under the test: a batch recovered after its start died records no
 * cards, and a failure record's tasks are planned apart from the test's own
 * starts (refinery_tree.generation_of), so no count of the test's own says
 * anything about them.
 */
export function reviewerSentence(generation: RefineryGeneration): string | null {
  if (generation.planned === 0) return generation.proven === 0 ? REFINERY_COPY.notPlanned : null
  const accepted = generation.accepted === 0 ? 'none' : generation.accepted.toString()
  return `The reviewer accepted ${accepted} of ${counted(generation.planned, 'card')}.`
}

/** The opened row's sentences: step 0, then the reviewer when it has one. */
export function testDetail(test: Pick<RefineryTest, 'base_policy_validation' | 'generation'>): string {
  const reviewer = reviewerSentence(test.generation)
  const stepZero = stepZeroSentence(test.base_policy_validation)
  return reviewer === null ? stepZero : `${stepZero} ${reviewer}`
}

/** One task's identity on the page: the api names a case by all three. */
export function taskKey(task: TaskCase): string {
  return `${task.batch}/${task.record_id}/${task.case}`
}

/** The staff requests the page cannot read, named by their bucket keys. */
export function unreadableRequestsLabel(keys: string[]): string {
  return `The next generation start fails on ${counted(keys.length, 'staff request')} the page cannot read: ${keys.join(', ')}`
}

/** The line beside the Show menu while it shows every test: the tree's counts. */
export function treeSummaryLabel(tests: number, tasks: number): string {
  return `${counted(tests, 'test')} and ${counted(tasks, 'proven task')}.`
}

/** The line beside the Show menu while it filters: how many tests are left of every test. */
export function shownTestsLabel(shown: number, total: number): string {
  return `${shown.toString()} of ${counted(total, 'test')} shown`
}

/** The Show menu's first option: every test, counted. */
export function allTestsOption(tests: number): string {
  return `All tests (${tests.toString()})`
}

/** The Show menu's option for the tests of one verdict. */
export function verdictOption(verdict: TestVerdict, tests: number): string {
  return `${REFINERY_COPY.verdicts[verdict]} (${counted(tests, 'test')})`
}

/** The Show menu's option for the tests the base policy fails at step 0. */
export function policyFailsOption(tests: number): string {
  return `Fails at step 0 (${counted(tests, 'test')})`
}

/** The Show menu's option for the tasks the base policy always passes. */
export function alwaysPassedOption(tasks: number): string {
  return `Always passes (${counted(tasks, 'task')})`
}

/** The folded line at the bottom of the page that opens the tasks under no test. */
export function unresolvedLabel(tasks: number): string {
  return `${counted(tasks, 'proven task')} under no test of this night`
}

/** The accessible name of a task's Files link, which opens its case files. */
export function filesLabel(task: Pick<RefineryTask, 'task_id'>): string {
  return `${REFINERY_COPY.files}: ${task.task_id}`
}

/** "1 test" or "3 tests". */
function counted(count: number, noun: string): string {
  return `${count.toString()} ${noun}${count === 1 ? '' : 's'}`
}

/** How many tests and tasks are ticked: "1 test and 3 tasks selected". */
export function selectedLabel(tests: number, tasks: number): string {
  const parts = [
    ...(tests > 0 ? [counted(tests, 'test')] : []),
    ...(tasks > 0 || tests === 0 ? [counted(tasks, 'task')] : []),
  ]
  return `${parts.join(' and ')} selected`
}

/** The accessible name of a task's checkbox. */
export function selectTaskLabel(task: Pick<RefineryTask, 'task_id'>): string {
  return `Select ${task.task_id}`
}

/** How a control names its test: the case name and the module, since two
 * tests of one case name (a Cartage file and its `.skills` sibling) are
 * different tests. */
export function testName(test: Pick<RefineryTest, 'family' | 'module'>): string {
  return `${test.family} (${test.module})`
}

/** A test's name cut where a line may break: before each capital of a
 * camelCase word and before a dot, so a long name wraps between its words. */
export function nameParts(name: string): string[] {
  return name.split(/(?=[A-Z.])/)
}

/** The accessible name of a test's checkbox, which ticks the test and every task under it. */
export function selectTestLabel(test: Pick<RefineryTest, 'family' | 'module'>): string {
  return `Select ${testName(test)}`
}

/** The chip on a test a generation start will plan again, with the cards staff asked for. */
export function regenerateLabel(regenerate: RefineryRegenerate): string {
  const label = REFINERY_COPY.regenerate[regenerate.state]
  return regenerate.cards === null ? label : `${label} (${counted(regenerate.cards, 'card')})`
}

/**
 * How the Generate tasks dialog names a ticked test: its case name, with
 * its path where another ticked test shares the name (`testName`).
 */
export function askedTestLabel(
  test: Pick<RefineryTest, 'family' | 'module'>,
  asked: Pick<RefineryTest, 'family' | 'module'>[],
): string {
  const shared = asked.filter((other) => other.family === test.family).length > 1
  return shared ? testName(test) : test.family
}

/** The Generate tasks dialog's question. */
export function generateTitle(tests: number): string {
  return `Generate tasks from ${counted(tests, 'test')}?`
}

/**
 * Why Generate only saves the request, above its button: the job row's
 * reason (`jobBlocker`, with `actions.running` for a run of the team's
 * that is going), then where the tests go.
 */
export function blockedLabel(reason: string): string {
  const sentence = /[.!?]$/.test(reason) ? reason : `${reason}.`
  return `${sentence} ${REFINERY_COPY.actions.nextRun}`
}

/** The Exclude dialog's question. */
export function excludeTitle(tasks: number): string {
  return `Exclude ${counted(tasks, 'task')}?`
}

/** The Include dialog's question. */
export function includeTitle(tasks: number): string {
  return `Include ${counted(tasks, 'task')}?`
}

/**
 * Where Generate ended: it started the run, it saved the request because
 * the job could not start, or it saved the request and the api refused
 * the run, with its reason when it gave one.
 */
export type GenerateResult = { kind: 'started' } | { kind: 'saved' } | { kind: 'refused'; reason: string | null }

/** The line under the bar once Generate lands, for the tests it asked about. */
export function generateResultLabel(result: GenerateResult, tests: number): string {
  if (result.kind === 'started') return `Task generation started for ${counted(tests, 'test')}.`
  if (result.kind === 'saved') return `${counted(tests, 'test')} ${tests === 1 ? 'waits' : 'wait'} for the next generation run.`
  const refused = 'The request is saved for the next run. Task generation did not start'
  return result.reason === null ? `${refused}.` : `${refused}: ${result.reason}`
}

/**
 * The most cards a regenerate request may ask per test: one gate batch
 * (task_generation/memory.py MAX_REQUESTED_CARDS, the gate's
 * TASK_GATE_MAX_BATCH_TASKS). The cards field's schema (tree.ts
 * `parseCards`) and its error read it here. The api checks it again.
 */
export const MAX_CARDS = 8

/** Every fixed string the page renders, so one test can gate them all. */
export const REFINERY_COPY = {
  title: 'Data refinery',
  description: 'See the tests of the latest scoring night and the proven tasks generated from them',
  benchmark: 'Benchmark',
  scored: 'Scored',
  noBenchmarks: 'The evals config names no benchmark.',
  loadFailed: 'Failed to load the tree:',
  noNightTitle: 'No scoring night to read',
  noMatch: 'No test or task matches this filter.',
  nightlyModel: 'Nightly rates on',
  show: {
    label: 'Show',
    verdict: 'Verdict',
    basePolicy: 'Base policy',
  },
  testColumns: {
    test: 'Test',
    nightly: 'Nightly',
    verdict: 'Verdict',
    proven: 'Proven',
  },
  taskColumns: {
    task: 'Task',
    passes: 'Reference',
    basePolicy: 'Base policy',
    datasets: 'Trained in',
  },
  verdicts: {
    model_limitation: 'Model limitation',
    flagged: 'Flagged',
    needs_human: 'Needs a human',
    inconclusive: 'Inconclusive',
    passing: 'Passing',
    noRound: 'No round yet',
  },
  heldOut: 'Held out',
  excluded: 'Excluded',
  notMeasured: 'Not measured',
  noEvalModel: 'No eval model',
  noStepZero: 'No GRPO run scored this test at step 0.',
  notPlanned: 'No generation run has planned the test yet.',
  files: 'Files',
  showFile: 'Show file',
  hideFile: 'Hide file',
  filesLoading: 'Loading the files',
  filesFailed: 'Failed to load the files:',
  fileLoading: 'Loading the file',
  fileFailed: 'Failed to load the file:',
  fileTruncated: 'The file is longer than shown here.',
  regenerate: {
    requested: 'Waiting for generation',
    flagged: 'Marked to generate again',
    deferred: 'Waiting for the next scoring night',
  },
  actions: {
    label: 'Staff actions',
    generate: 'Generate tasks',
    exclude: 'Exclude',
    include: 'Include',
    clear: 'Clear',
    cardsLabel: 'Cards per test',
    cardsPlaceholder: 'Default',
    cardsInvalid: `Enter a whole number from 1 to ${MAX_CARDS.toString()}.`,
    excludeDescription: 'No dataset build trains on an excluded task.',
    includeDescription: 'The next dataset build may train on an included task again.',
    running: 'Task generation is running.',
    nextRun: 'These tests go into the next run.',
    confirmGenerate: 'Generate',
    saving: 'Saving...',
    starting: 'Starting...',
  },
  /** What a staff action says when the api refuses it with no detail. */
  failed: {
    regenerate: 'The tests were not requested. Try again.',
    exclusions: 'The exclusions were not saved. Try again.',
  },
} as const
